// Phase 4 — "also launch on other platforms" approval-time replication.
//
// Pure helpers (no React) backing the replicate checkboxes in the approve
// confirm dialog. The backend contract (central-api feat/creative-assets)
// accepts POST /employee/tara/actions/proposals/{id}/replicate with
// `platforms: string[]` drawn from the five canonical platform keys below.
// Replication is only valid for CREATIVE proposals — action_key ending
// '.ad.create' / '.ad.create_rsa' — and only AFTER approve succeeded.
import type {
  Proposal,
  ReplicationResult,
} from '../../../lib/api/proposals/proposals-api';

/** Canonical platform keys accepted by the replicate endpoint. */
export const REPLICATION_PLATFORM_KEYS = [
  'facebook',
  'tiktok',
  'snapchat',
  'google',
  'linkedin',
] as const;

export type ReplicationPlatformKey = typeof REPLICATION_PLATFORM_KEYS[number];

/** Display labels for the replicate checkboxes / result lines. */
export const REPLICATION_PLATFORM_LABELS: Record<
  ReplicationPlatformKey,
  string
> = {
  facebook: 'Facebook',
  tiktok: 'TikTok',
  snapchat: 'Snapchat',
  google: 'Google',
  linkedin: 'LinkedIn',
};

export function replicationPlatformLabel(key: string): string {
  return (
    REPLICATION_PLATFORM_LABELS[key as ReplicationPlatformKey] ||
    key.charAt(0).toUpperCase() + key.slice(1)
  );
}

/** True when an action key denotes a creative launch (replicable) action. */
export function isCreativeActionKey(actionKey: string | undefined): boolean {
  if (!actionKey) return false;
  return (
    actionKey.endsWith('.ad.create') || actionKey.endsWith('.ad.create_rsa')
  );
}

/**
 * A proposal is "creative" (eligible for cross-platform replication) when its
 * action key is a creative launch key OR it carries creative previews. The
 * preview fallback covers older backend rows whose action_type relation
 * didn't hydrate on the list payload.
 */
export function isCreativeProposal(proposal: Proposal): boolean {
  if (isCreativeActionKey(proposal.action_type?.action_key)) return true;
  return (proposal.creative_previews?.length ?? 0) > 0;
}

// Proposal.platform slugs ('facebook_ads') → canonical replicate keys.
const PLATFORM_SLUG_TO_KEY: Record<string, ReplicationPlatformKey> = {
  facebook: 'facebook',
  facebook_ads: 'facebook',
  meta: 'facebook',
  meta_ads: 'facebook',
  instagram: 'facebook',
  tiktok: 'tiktok',
  tiktok_ads: 'tiktok',
  snapchat: 'snapchat',
  snapchat_ads: 'snapchat',
  snap: 'snapchat',
  google: 'google',
  google_ads: 'google',
  linkedin: 'linkedin',
  linkedin_ads: 'linkedin',
};

/**
 * Map a Proposal.platform slug to its canonical replicate platform key.
 * Returns null for unknown platforms (they simply don't shrink the target
 * list — the backend skips an already-covered platform anyway).
 */
export function replicationPlatformKey(
  platform: string | undefined
): ReplicationPlatformKey | null {
  if (!platform) return null;
  return PLATFORM_SLUG_TO_KEY[platform.toLowerCase()] ?? null;
}

/**
 * Platforms offered as replication targets for a single proposal: the five
 * canonical keys minus the proposal's own platform.
 */
export function replicationTargetsFor(
  proposal: Proposal
): ReplicationPlatformKey[] {
  const own = replicationPlatformKey(proposal.platform);
  return REPLICATION_PLATFORM_KEYS.filter((key) => key !== own);
}

/**
 * Platforms offered in the BATCH confirm dialog. A key stays on offer as
 * long as at least one creative item could replicate to it, i.e. it is only
 * dropped when EVERY creative item already lives on that platform.
 */
export function replicationTargetsForBatch(
  creativeProposals: Proposal[]
): ReplicationPlatformKey[] {
  if (creativeProposals.length === 0) return [];
  return REPLICATION_PLATFORM_KEYS.filter((key) =>
    creativeProposals.some((p) => replicationPlatformKey(p.platform) !== key)
  );
}

/**
 * The subset of ticked platforms actually sent for one item — its own
 * platform is filtered out (relevant in mixed-platform batches where the
 * shared checkbox row may include another item's home platform).
 */
export function platformsToReplicate(
  proposal: Proposal,
  ticked: Iterable<string>
): ReplicationPlatformKey[] {
  const own = replicationPlatformKey(proposal.platform);
  const set = new Set(ticked);
  return REPLICATION_PLATFORM_KEYS.filter((key) => key !== own && set.has(key));
}

/** Glyph and row modifier per replicate outcome; unknown ones stay neutral. */
const REPLICATION_OUTCOME: Record<string, { glyph: string; tone: string }> = {
  proposal_created: { glyph: 'check', tone: 'proposal_created' },
  request_queued: { glyph: 'clock', tone: 'request_queued' },
  proposal_exists: { glyph: 'info', tone: 'info' },
  skipped: { glyph: 'x', tone: 'skipped' },
};
const NEUTRAL_OUTCOME = { glyph: 'info', tone: 'info' };

export function replicationOutcome(status: string | undefined): {
  glyph: string;
  tone: string;
} {
  return (
    (status && Object.prototype.hasOwnProperty.call(REPLICATION_OUTCOME, status)
      ? REPLICATION_OUTCOME[status]
      : undefined) || NEUTRAL_OUTCOME
  );
}

/**
 * One human line per replicate outcome. 'skipped' carries the backend's
 * reason; a status this client doesn't know yet gets a neutral line.
 */
export function replicationLine(
  r: ReplicationResult,
  t: (key: string, params?: Record<string, string>) => string
): string {
  switch (r.status) {
    case 'proposal_created':
      return t('ReplicationCreated');
    case 'request_queued':
      return t('ReplicationQueued', {
        platform: replicationPlatformLabel(r.platform),
      });
    case 'proposal_exists':
      return t('ReplicationExists');
    case 'skipped':
      return r.reason || t('ReplicationSkipped');
    default:
      return r.reason || t('ReplicationOther');
  }
}
